/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * API Reference Page
 *
 * Interactive API documentation viewer for LUKHAS services
 * Integrates with FastAPI auto-generated OpenAPI specs
 */

import React, { useState, useEffect } from 'react'
import { Book, Server, Code, ChevronRight, ExternalLink, Play } from 'lucide-react'

interface APIService {
  name: string
  description: string
  baseUrl: string
  docsUrl: string
  port: number
  status: 'online' | 'offline' | 'checking'
  endpoints: APIEndpoint[]
  color: string
}

interface APIEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  path: string
  summary: string
  description: string
  tags: string[]
  requestBody?: {
    required: boolean
    schema: any
  }
  responses: {
    [key: string]: {
      description: string
      schema?: any
    }
  }
}

// API Services Configuration
const API_SERVICES: APIService[] = [
  {
    name: 'Terminal Service',
    description: 'Web-based terminal execution with sandbox and bastion support',
    baseUrl: 'http://localhost:8085',
    docsUrl: 'http://localhost:8085/docs',
    port: 8085,
    status: 'checking',
    color: 'blue',
    endpoints: [
      {
        method: 'POST',
        path: '/api/terminal/session',
        summary: 'Create Terminal Session',
        description: 'Create a new terminal session with specified execution mode (local/sandbox/bastion)',
        tags: ['Terminal', 'Sessions'],
        requestBody: {
          required: true,
          schema: {
            mode: 'local | sandbox | bastion',
          },
        },
        responses: {
          '200': {
            description: 'Session created successfully',
            schema: {
              session_id: 'string',
              mode: 'string',
              created_at: 'string',
              status: 'active | inactive',
            },
          },
        },
      },
      {
        method: 'POST',
        path: '/api/terminal/exec',
        summary: 'Execute Command',
        description: 'Execute a command in an existing terminal session (buffered execution)',
        tags: ['Terminal', 'Execution'],
        requestBody: {
          required: true,
          schema: {
            session_id: 'string',
            command: 'string',
          },
        },
        responses: {
          '200': {
            description: 'Command executed',
            schema: {
              stdout: 'string',
              stderr: 'string',
              exit_code: 'number',
              execution_time: 'number',
            },
          },
        },
      },
      {
        method: 'GET',
        path: '/api/terminal/exec-stream',
        summary: 'Stream Command Output',
        description: 'Execute command with SSE streaming for real-time output (long-running commands)',
        tags: ['Terminal', 'Streaming'],
        responses: {
          '200': {
            description: 'SSE stream of command output',
            schema: {
              type: 'stdout | stderr | exit',
              data: 'string',
              code: 'number (for exit events)',
            },
          },
        },
      },
      {
        method: 'DELETE',
        path: '/api/terminal/session/{session_id}',
        summary: 'Close Session',
        description: 'Close an active terminal session and clean up resources',
        tags: ['Terminal', 'Sessions'],
        responses: {
          '200': { description: 'Session closed successfully' },
        },
      },
      {
        method: 'GET',
        path: '/api/terminal/history/{session_id}',
        summary: 'Get Command History',
        description: 'Retrieve command history for a session',
        tags: ['Terminal', 'History'],
        responses: {
          '200': {
            description: 'Command history retrieved',
            schema: {
              history: 'string[]',
            },
          },
        },
      },
    ],
  },
  {
    name: 'Editor Service',
    description: 'Collaborative code/markdown editor with version control and GitHub integration',
    baseUrl: 'http://localhost:8000',
    docsUrl: 'http://localhost:8000/docs',
    port: 8000,
    status: 'checking',
    color: 'green',
    endpoints: [
      {
        method: 'POST',
        path: '/api/editor/draft',
        summary: 'Create or Update Draft',
        description: 'Create a new draft or update an existing one with markdown content',
        tags: ['Editor', 'Drafts'],
        requestBody: {
          required: true,
          schema: {
            id: 'string (optional)',
            title: 'string',
            markdown: 'string',
            json_ast: 'object',
            tenant_id: 'string',
            user_id: 'string',
          },
        },
        responses: {
          '200': {
            description: 'Draft saved successfully',
            schema: {
              id: 'string',
              title: 'string',
              markdown: 'string',
              created_at: 'string',
              updated_at: 'string',
            },
          },
        },
      },
      {
        method: 'GET',
        path: '/api/editor/draft/{id}',
        summary: 'Get Draft',
        description: 'Retrieve a specific draft by ID',
        tags: ['Editor', 'Drafts'],
        responses: {
          '200': { description: 'Draft retrieved' },
        },
      },
      {
        method: 'GET',
        path: '/api/editor/drafts',
        summary: 'List Drafts',
        description: 'List all drafts for the current user',
        tags: ['Editor', 'Drafts'],
        responses: {
          '200': {
            description: 'Drafts listed',
            schema: {
              drafts: 'Draft[]',
            },
          },
        },
      },
      {
        method: 'GET',
        path: '/api/editor/draft/{draft_id}/history',
        summary: 'Get Version History',
        description: 'Retrieve version history for a draft',
        tags: ['Editor', 'Versions'],
        responses: {
          '200': {
            description: 'Version history retrieved',
            schema: {
              versions: 'DraftVersion[]',
            },
          },
        },
      },
      {
        method: 'POST',
        path: '/api/editor/draft/{draft_id}/revert',
        summary: 'Revert to Version',
        description: 'Revert a draft to a specific version',
        tags: ['Editor', 'Versions'],
        requestBody: {
          required: true,
          schema: {
            version_id: 'number',
          },
        },
        responses: {
          '200': { description: 'Draft reverted successfully' },
        },
      },
      {
        method: 'GET',
        path: '/api/editor/export/{draft_id}',
        summary: 'Export Draft',
        description: 'Export draft in various formats (md, json, zip)',
        tags: ['Editor', 'Export'],
        responses: {
          '200': { description: 'Draft exported as file blob' },
        },
      },
      {
        method: 'POST',
        path: '/api/editor/publish',
        summary: 'Publish to GitHub',
        description: 'Publish draft as GitHub pull request',
        tags: ['Editor', 'GitHub'],
        requestBody: {
          required: true,
          schema: {
            draft_id: 'string',
            repo: 'string (owner/repo)',
            branch: 'string',
            pr_title: 'string',
          },
        },
        responses: {
          '200': {
            description: 'PR created successfully',
            schema: {
              pr_url: 'string',
            },
          },
        },
      },
    ],
  },
  {
    name: 'NIΛS Service',
    description: 'Non-Intrusive Lambda Symbolic system for Dream Commerce',
    baseUrl: 'http://localhost:8086',
    docsUrl: 'http://localhost:8086/docs',
    port: 8086,
    status: 'checking',
    color: 'purple',
    endpoints: [
      {
        method: 'POST',
        path: '/api/nias/register',
        summary: 'Register User',
        description: 'Register a new user with NIΛS Dream Commerce system',
        tags: ['NIΛS', 'Users'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            tier: 'PUBLIC | PERSONAL | CREATIVE | ENTERPRISE | QUANTUM',
            consent_level: 'NONE | BASIC | ENHANCED | FULL_SYMBOLIC | DREAM_AWARE',
          },
        },
        responses: {
          '200': { description: 'User registered successfully' },
        },
      },
      {
        method: 'POST',
        path: '/api/nias/consent',
        summary: 'Update Consent',
        description: 'Update user consent level for Dream Commerce',
        tags: ['NIΛS', 'Consent'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            consent_level: 'NONE | BASIC | ENHANCED | FULL_SYMBOLIC | DREAM_AWARE',
          },
        },
        responses: {
          '200': { description: 'Consent updated' },
        },
      },
      {
        method: 'POST',
        path: '/api/nias/emotional-state',
        summary: 'Update Emotional State',
        description: 'Update user emotional vector for contextual message delivery',
        tags: ['NIΛS', 'Context'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            emotional_vector: 'object (CALM, FOCUSED, CREATIVE, STRESSED, OVERWHELMED, DREAMING)',
          },
        },
        responses: {
          '200': { description: 'Emotional state updated' },
        },
      },
      {
        method: 'POST',
        path: '/api/nias/messages',
        summary: 'Deliver Symbolic Message',
        description: 'Deliver a symbolic message with lambda signature',
        tags: ['NIΛS', 'Messages'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            content: 'string',
            tags: 'string[]',
            tier: 'PUBLIC | PERSONAL | CREATIVE | ENTERPRISE | QUANTUM',
            emotional_tone: 'CALM | FOCUSED | CREATIVE | STRESSED | OVERWHELMED | DREAMING',
            dream_based: 'boolean',
          },
        },
        responses: {
          '200': {
            description: 'Message delivery result',
            schema: {
              status: 'delivered | blocked | deferred | transformed',
              message_id: 'string',
              lambda_signature: 'string',
            },
          },
        },
      },
      {
        method: 'GET',
        path: '/api/nias/messages/stream/{user_id}',
        summary: 'Stream Messages',
        description: 'SSE stream for real-time symbolic message delivery',
        tags: ['NIΛS', 'Streaming'],
        responses: {
          '200': {
            description: 'SSE stream of symbolic messages',
          },
        },
      },
      {
        method: 'POST',
        path: '/api/nias/dream-session',
        summary: 'Process Dream Session',
        description: 'Process queued dream deliveries for user',
        tags: ['NIΛS', 'Dreams'],
        responses: {
          '200': { description: 'Dream session processed' },
        },
      },
    ],
  },
  {
    name: 'DΛST Service',
    description: 'Dynamic Lambda Symbol Tracker for contextual awareness',
    baseUrl: 'http://localhost:8087',
    docsUrl: 'http://localhost:8087/docs',
    port: 8087,
    status: 'checking',
    color: 'cyan',
    endpoints: [
      {
        method: 'POST',
        path: '/api/dast/register',
        summary: 'Register User',
        description: 'Register a new user with DΛST symbol tracking',
        tags: ['DΛST', 'Users'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            initial_tags: 'string[] (optional)',
          },
        },
        responses: {
          '200': { description: 'User registered successfully' },
        },
      },
      {
        method: 'POST',
        path: '/api/dast/symbols/add',
        summary: 'Add Symbol',
        description: 'Add a symbolic tag to user context',
        tags: ['DΛST', 'Symbols'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            symbol: 'string',
            category: 'ACTIVITY | CONTEXT | MOOD | FOCUS | GOAL | etc',
            source: 'USER_EXPLICIT | CALENDAR | ACTIVITY_TRACKER | etc',
            confidence: 'number (0-1)',
          },
        },
        responses: {
          '200': { description: 'Symbol added successfully' },
        },
      },
      {
        method: 'POST',
        path: '/api/dast/symbols/remove',
        summary: 'Remove Symbol',
        description: 'Remove a symbolic tag from user context',
        tags: ['DΛST', 'Symbols'],
        responses: {
          '200': { description: 'Symbol removed successfully' },
        },
      },
      {
        method: 'POST',
        path: '/api/dast/symbols/query',
        summary: 'Query Symbols',
        description: 'Query current active symbols with filtering',
        tags: ['DΛST', 'Symbols'],
        requestBody: {
          required: true,
          schema: {
            user_id: 'string',
            category_filter: 'string (optional)',
            confidence_threshold: 'number (optional)',
          },
        },
        responses: {
          '200': {
            description: 'Symbols retrieved',
            schema: {
              symbols: 'string[]',
            },
          },
        },
      },
      {
        method: 'GET',
        path: '/api/dast/context/{user_id}',
        summary: 'Get Context Snapshot',
        description: 'Get comprehensive context snapshot with all active symbols',
        tags: ['DΛST', 'Context'],
        responses: {
          '200': {
            description: 'Context snapshot retrieved',
            schema: {
              tags: 'SymbolicTag[]',
              focus_score: 'number',
              coherence_score: 'number',
              stability_score: 'number',
              lambda_fingerprint: 'string',
            },
          },
        },
      },
      {
        method: 'GET',
        path: '/api/dast/context/{user_id}/stream',
        summary: 'Stream Context Updates',
        description: 'SSE stream for real-time context updates',
        tags: ['DΛST', 'Streaming'],
        responses: {
          '200': { description: 'SSE stream of context snapshots' },
        },
      },
      {
        method: 'POST',
        path: '/api/dast/integration/calendar',
        summary: 'Integrate Calendar',
        description: 'Integrate calendar events to infer symbolic context',
        tags: ['DΛST', 'Integration'],
        responses: {
          '200': { description: 'Calendar events processed' },
        },
      },
      {
        method: 'POST',
        path: '/api/dast/integration/activity',
        summary: 'Integrate Activity Data',
        description: 'Integrate activity tracking data',
        tags: ['DΛST', 'Integration'],
        responses: {
          '200': { description: 'Activity data processed' },
        },
      },
    ],
  },
]

export const APIReferencePage: React.FC = () => {
  const [services, setServices] = useState<APIService[]>(API_SERVICES)
  const [selectedService, setSelectedService] = useState<APIService>(API_SERVICES[0])
  const [, setSelectedEndpoint] = useState<APIEndpoint | null>(null)
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set())

  // Check service health on mount
  useEffect(() => {
    const checkHealth = async () => {
      const updatedServices = await Promise.all(
        services.map(async (service) => {
          try {
            const response = await fetch(`${service.baseUrl}/health`, { method: 'GET' })
            return { ...service, status: response.ok ? 'online' : 'offline' } as APIService
          } catch {
            return { ...service, status: 'offline' } as APIService
          }
        })
      )
      setServices(updatedServices)
    }

    checkHealth()
  }, [])

  const toggleEndpoint = (path: string) => {
    const newExpanded = new Set(expandedEndpoints)
    if (newExpanded.has(path)) {
      newExpanded.delete(path)
    } else {
      newExpanded.add(path)
    }
    setExpandedEndpoints(newExpanded)
  }

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      POST: 'bg-green-500/20 text-green-400 border-green-500/30',
      PUT: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      DELETE: 'bg-red-500/20 text-red-400 border-red-500/30',
      PATCH: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    }
    return colors[method] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  }

  const getServiceColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'from-blue-500/20 to-cyan-500/20',
      green: 'from-green-500/20 to-emerald-500/20',
      purple: 'from-purple-500/20 to-pink-500/20',
      cyan: 'from-cyan-500/20 to-teal-500/20',
    }
    return colors[color] || 'from-gray-500/20 to-gray-600/20'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                <Book className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">API Reference</h1>
                <p className="text-gray-400 mt-1">Λ Interactive Service Documentation</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{services.length}</p>
                <p className="text-xs text-gray-400">Services</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Service Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                Services
              </h3>

              {services.map((service) => (
                <button
                  key={service.name}
                  onClick={() => {
                    setSelectedService(service)
                    setSelectedEndpoint(null)
                  }}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    selectedService.name === service.name
                      ? 'bg-gray-800/50 border-blue-500/50'
                      : 'bg-gray-900/50 border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Server className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-white text-sm">{service.name}</span>
                    </div>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        service.status === 'online'
                          ? 'bg-green-500'
                          : service.status === 'offline'
                          ? 'bg-red-500'
                          : 'bg-yellow-500 animate-pulse'
                      }`}
                    />
                  </div>
                  <p className="text-xs text-gray-400">Port {service.port}</p>
                  <p className="text-xs text-gray-500 mt-1">{service.endpoints.length} endpoints</p>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Service Overview */}
            <div className={`p-6 bg-gradient-to-br ${getServiceColor(selectedService.color)} border border-gray-800 rounded-lg`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedService.name}</h2>
                  <p className="text-gray-300">{selectedService.description}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedService.status === 'online'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {selectedService.status === 'online' ? 'Online' : 'Offline'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-900/50 p-3 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Base URL</p>
                  <code className="text-sm text-gray-300 font-mono">{selectedService.baseUrl}</code>
                </div>
                <div className="bg-gray-900/50 p-3 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">OpenAPI Docs</p>
                  <a
                    href={selectedService.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                  >
                    <span>/docs</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

            {/* Endpoints List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Endpoints</h3>

              {selectedService.endpoints.map((endpoint, index) => {
                const isExpanded = expandedEndpoints.has(endpoint.path)

                return (
                  <div
                    key={index}
                    className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden hover:border-gray-700 transition-all"
                  >
                    {/* Endpoint Header */}
                    <button
                      onClick={() => toggleEndpoint(endpoint.path)}
                      className="w-full p-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <span
                          className={`px-3 py-1 rounded text-xs font-bold border ${getMethodColor(
                            endpoint.method
                          )}`}
                        >
                          {endpoint.method}
                        </span>
                        <code className="text-sm text-gray-300 font-mono">{endpoint.path}</code>
                      </div>

                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-400">{endpoint.summary}</span>
                        <ChevronRight
                          className={`w-5 h-5 text-gray-400 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                        />
                      </div>
                    </button>

                    {/* Endpoint Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-800 p-4 space-y-4">
                        {/* Description */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">Description</h4>
                          <p className="text-sm text-gray-400">{endpoint.description}</p>
                        </div>

                        {/* Tags */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">Tags</h4>
                          <div className="flex flex-wrap gap-2">
                            {endpoint.tags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="px-2 py-1 bg-gray-800/50 text-gray-400 text-xs rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Request Body */}
                        {endpoint.requestBody && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-300 mb-2">
                              Request Body
                              {endpoint.requestBody.required && (
                                <span className="ml-2 text-xs text-red-400">(required)</span>
                              )}
                            </h4>
                            <div className="bg-gray-950/50 border border-gray-800 rounded p-3">
                              <pre className="text-xs text-gray-300 font-mono overflow-x-auto">
                                {JSON.stringify(endpoint.requestBody.schema, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}

                        {/* Responses */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">Responses</h4>
                          <div className="space-y-2">
                            {Object.entries(endpoint.responses).map(([code, response]) => (
                              <div key={code} className="bg-gray-950/50 border border-gray-800 rounded p-3">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span
                                    className={`px-2 py-0.5 rounded text-xs font-bold ${
                                      code.startsWith('2')
                                        ? 'bg-green-500/20 text-green-400'
                                        : 'bg-red-500/20 text-red-400'
                                    }`}
                                  >
                                    {code}
                                  </span>
                                  <span className="text-xs text-gray-400">{response.description}</span>
                                </div>
                                {response.schema && (
                                  <pre className="text-xs text-gray-300 font-mono overflow-x-auto mt-2">
                                    {JSON.stringify(response.schema, null, 2)}
                                  </pre>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Try It Button */}
                        <div className="pt-2">
                          <a
                            href={`${selectedService.docsUrl}#${endpoint.path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                          >
                            <Play className="w-4 h-4" />
                            <span>Try in OpenAPI Docs</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Integration Examples */}
            <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-400 mb-4 flex items-center space-x-2">
                <Code className="w-5 h-5" />
                <span>Integration Example</span>
              </h3>

              <div className="bg-gray-950/50 border border-gray-800 rounded p-4">
                <pre className="text-sm text-gray-300 font-mono overflow-x-auto">
{`import { ${selectedService.name.replace(' Service', 'API')} } from '@lukhas/api-client'

// Create session/connection
const result = await ${selectedService.name.replace(' Service', 'API')}.${
  selectedService.endpoints[0]?.summary.toLowerCase().replace(/\s+/g, '')
}(...)

// Handle result
console.log(result)`}
                </pre>
              </div>

              <p className="text-xs text-gray-400 mt-3">
                See{' '}
                <code className="px-2 py-0.5 bg-gray-800 rounded">
                  websites/packages/lukhas.dev/src/lib/api-client.ts
                </code>{' '}
                for full TypeScript client implementation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default APIReferencePage
